import type { ResultStatus } from '../lib/types';

/**
 * The family-status rose.
 *
 * One wedge per family, arranged around a ring and divided radially into its
 * in-scope requirements, each cell coloured by status. The centre carries the
 * overall percentage met.
 *
 * It earns its place over a bar chart because it shows two things at once that
 * a bar chart shows separately: how much of each family is done, and where the
 * gaps cluster. A framework with one badly-neglected family looks completely
 * different here from one that is evenly half-finished, even though both score
 * 50%.
 *
 * FLAT, deliberately. This was previously drawn as a tilted, extruded disc —
 * an axonometric projection with a shadow and a coloured rim. Two reasons that
 * went:
 *
 *   - It read as dated. Extruded 3D pies are the visual language of a 2007
 *     dashboard, and the drop shadow and darkened rim were doing decorative
 *     work no reader needed.
 *
 *   - More importantly it was distorting. Tilting a polar chart foreshortens
 *     the far side, so a wedge at the back looked smaller than an identical
 *     one at the front, and the top and bottom of the ring could not be
 *     compared by eye. The old comment acknowledged this and pointed at the
 *     per-family bars as "the exact read" — which is an odd thing to have to
 *     say about your own chart. Flat, every cell of the same rank is the same
 *     size wherever it sits, so the picture and the bars finally agree.
 *
 * Circle geometry is now literal: an arc of radius r is an arc of radius r.
 *
 * Colours come from CSS classes rather than fill attributes so the chart
 * follows the theme — a `fill="var(--x)"` attribute does not resolve, and a
 * hard-coded hex would be the one thing on the page that ignores dark mode.
 */

export interface RoseFamily {
  FamilyId: number;
  Code: string;
  Title: string;
  /** In-scope requirements, in order, each contributing one cell. */
  cells: { RequirementId: number; Code: string; Title: string; Status: ResultStatus }[];
}

const TAU = Math.PI * 2;

/** Angle 0 at the top, going clockwise. */
function project(cx: number, cy: number, r: number, a: number): [number, number] {
  return [cx + r * Math.sin(a), cy - r * Math.cos(a)];
}

/** An annular sector — the shape of one requirement cell. */
function sector(cx: number, cy: number, rIn: number, rOut: number, a0: number, a1: number): string {
  const [x0o, y0o] = project(cx, cy, rOut, a0);
  const [x1o, y1o] = project(cx, cy, rOut, a1);
  const [x1i, y1i] = project(cx, cy, rIn, a1);
  const [x0i, y0i] = project(cx, cy, rIn, a0);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return (
    `M${x0o},${y0o} A${rOut},${rOut} 0 ${large} 1 ${x1o},${y1o} ` +
    `L${x1i},${y1i} A${rIn},${rIn} 0 ${large} 0 ${x0i},${y0i} Z`
  );
}

/** "Minimum Maturity" → "Minimum". A one-word title is left alone. */
function shortLabel(family: RoseFamily, ordinal: number): string {
  const trimmed = family.Title.replace(/\s+(Maturity|Controls|Requirements)$/i, '').trim();
  /* Numeric family codes read better as their ordinal on the ring; codes that
     are already short words (AC, MFA, GOV) are more useful than a number. */
  if (/^[\d.]+$/.test(family.Code)) return String(ordinal);
  if (family.Code.length <= 4) return family.Code;
  return trimmed || family.Code;
}

export function RoseChart({
  families,
  overallPct,
  onSelectFamily,
}: {
  families: RoseFamily[];
  /** Null when nothing is applicable — the dial shows a dash, not a zero. */
  overallPct: number | null;
  onSelectFamily?: (familyId: number) => void;
}) {
  const cx = 280;
  const cy = 250;
  const rInner = 96;
  const rOuter = 250;
  const rLabel = rOuter + 16;
  const gap = 0.012;
  const n = families.length;
  const wedge = n > 0 ? TAU / n : TAU;

  /* Spelled-out labels need room either side of the ring; ordinals do not. */
  const labels = families.map((f, i) => shortLabel(f, i + 1));
  const spelled = labels.some((l) => l.length > 3);
  const padX = spelled ? 96 : 20;
  const padY = 22;

  const viewBox = [
    cx - rOuter - padX,
    cy - rOuter - padY,
    2 * (rOuter + padX),
    2 * (rOuter + padY),
  ].join(' ');

  const pct = overallPct ?? 0;
  const dialClass =
    overallPct === null ? 'none' : pct >= 80 ? 'good' : pct >= 50 ? 'partial' : 'poor';
  const dialR = rInner - 8;

  /* The progress ring, as one arc. Now the geometry is flat this could be a
     dash array, but an explicit arc keeps the start angle at the top without
     a counter-rotation transform. */
  const arcAngle = (pct / 100) * TAU;
  const [ax0, ay0] = project(cx, cy, dialR, 0);
  const [ax1, ay1] = project(cx, cy, dialR, arcAngle);
  const arcPath =
    pct >= 99.95
      ? `M${ax0},${ay0} A${dialR},${dialR} 0 1 1 ${cx - 0.01},${ay0} A${dialR},${dialR} 0 1 1 ${ax0},${ay0}`
      : `M${ax0},${ay0} A${dialR},${dialR} 0 ${arcAngle > Math.PI ? 1 : 0} 1 ${ax1},${ay1}`;

  return (
    <div className="rose">
      <svg
        viewBox={viewBox}
        role="img"
        aria-label={
          overallPct === null ? 'Nothing applicable to score' : `Overall compliance ${pct}% met`
        }
      >
        {[1, 2].map((i) => (
          <circle
            key={i}
            className="rose-guide"
            cx={cx}
            cy={cy}
            r={rInner + ((rOuter - rInner) * i) / 3}
            fill="none"
          />
        ))}

        {families.map((family, i) => {
          const a0 = i * wedge + gap / 2;
          const a1 = (i + 1) * wedge - gap / 2;
          const band = rOuter - rInner;
          const midA = (a0 + a1) / 2;
          const [lx, ly] = project(cx, cy, rLabel, midA);
          const label = labels[i] ?? '';

          return (
            <g key={family.FamilyId}>
              {family.cells.length === 0 ? (
                /* No in-scope requirements at this level — a hollow wedge says
                   "nothing to do here", which is different from "nothing done". */
                <path className="rose-empty" d={sector(cx, cy, rInner, rOuter, a0, a1)} />
              ) : (
                family.cells.map((cell, j) => {
                  const cellHeight = band / family.cells.length;
                  const rIn = rInner + j * cellHeight;
                  const rOut = rInner + (j + 1) * cellHeight - 1.4;
                  return (
                    <path
                      key={cell.RequirementId}
                      className={`rose-cell ${cell.Status.replace('_', '-')}`}
                      d={sector(cx, cy, rIn, rOut, a0, a1)}
                      onClick={() => onSelectFamily?.(family.FamilyId)}
                    >
                      <title>
                        {cell.Code} {cell.Title} — {cell.Status.replace(/_/g, ' ')}
                      </title>
                    </path>
                  );
                })
              )}

              {/* Labels stay upright. Tangential text on a tilted ring reads as
                  a mistake rather than as perspective. */}
              <text
                className="rose-label"
                x={lx}
                y={ly - Math.cos(midA) * 6}
                fontSize={spelled ? 13 : 12}
                dominantBaseline="middle"
                textAnchor={lx > cx + 8 ? 'start' : lx < cx - 8 ? 'end' : 'middle'}
                onClick={() => onSelectFamily?.(family.FamilyId)}
              >
                {label}
                <title>
                  {family.Code} — {family.Title}
                </title>
              </text>
            </g>
          );
        })}

        {/* The rim, over the cells. Without it the ring has no edge — the
            outer cells just stop, and a run of pale "not assessed" ones stops
            invisibly. Drawn after the wedges so it closes the shape rather
            than being painted over by it. */}
        <circle className="rose-rim" cx={cx} cy={cy} r={rOuter} />
        <circle className="rose-rim" cx={cx} cy={cy} r={rInner} />

        {/* The hub reads as solid so the centre is a face, not a hole. */}
        <circle className="rose-dial" cx={cx} cy={cy} r={dialR} />
        <circle className="rose-track" cx={cx} cy={cy} r={dialR} fill="none" strokeWidth={6} />
        {pct > 0 && (
          <path
            className={`rose-arc ${dialClass}`}
            d={arcPath}
            fill="none"
            strokeWidth={6}
            strokeLinecap="round"
          />
        )}

        <text className="rose-pct" x={cx} y={cy + 4} textAnchor="middle">
          {overallPct === null ? '—' : `${pct}%`}
        </text>
        <text className={`rose-cap ${dialClass}`} x={cx} y={cy + 26} textAnchor="middle">
          MET
        </text>
      </svg>
    </div>
  );
}

export const STATUS_LABELS: Record<ResultStatus, string> = {
  met: 'Met',
  partial: 'Partially met',
  not_met: 'Not met',
  not_applicable: 'Not applicable',
  not_assessed: 'Not assessed',
};

/** The legend, and the same five colours used by the status buttons. */
export function RoseLegend({ tally }: { tally: Record<ResultStatus, number> }) {
  const order: ResultStatus[] = ['met', 'partial', 'not_met', 'not_applicable', 'not_assessed'];
  return (
    <div className="rose-legend">
      {order.map((status) => (
        <div key={status} className="rose-legend-item">
          <span className={`swatch ${status.replace('_', '-')}`} aria-hidden="true" />
          <span className="xs">{STATUS_LABELS[status]}</span>
          <span className="mono xs strong">{tally[status]}</span>
        </div>
      ))}
    </div>
  );
}
